import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FiMessageCircle, FiSend } from "react-icons/fi";
import "./TaskComments.css";

const TaskComments = ({ task, onAddComment, currentUser }) => {
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const timeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;

    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: date.getFullYear() !== now.getFullYear() ? "numeric" : undefined,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!comment.trim()) return;

    setSubmitting(true);
    try {
      await onAddComment(comment);
      setComment("");
    } catch (error) { /* ignored */ } finally {
      setSubmitting(false);
    }
  };

  const comments = task.comments || [];

  return (
    <div className="rtcm-container">
      {/* Comments List */}
      <div className="rtcm-list">
        {comments.length === 0 ? (
          <div className="rtcm-empty">
            <div className="rtcm-empty-icon">
              <FiMessageCircle size={32} />
            </div>
            <p>No comments yet</p>
            <span>Start the discussion below</span>
          </div>
        ) : (
          <AnimatePresence>
            {comments.map((cmt, index) => (
              <motion.div
                key={index}
                className="rtcm-item"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                <div className="rtcm-avatar">
                  {cmt.user?.profilePhotoUrl ? (
                    <img
                      src={cmt.user.profilePhotoUrl}
                      alt={cmt.user.name}
                      onError={(e) => {
                        e.target.style.display = "none";
                        e.target.nextSibling.style.display = "flex";
                      }}
                    />
                  ) : null}
                  <div 
                    className="rtcm-avatar-placeholder"
                    style={{ display: cmt.user?.profilePhotoUrl ? "none" : "flex" }}
                  >
                    {cmt.user?.name?.charAt(0).toUpperCase()}
                  </div>
                </div>

                <div className="rtcm-content">
                  <div className="rtcm-header">
                    <span className="rtcm-author">{cmt.user?.name || "Unknown"}</span>
                    <span className="rtcm-time">{timeAgo(cmt.createdAt)}</span>
                  </div>
                  <p className="rtcm-text">{cmt.content}</p>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        )}
      </div>

      {/* Add Comment Form */}
      <form onSubmit={handleSubmit} className="rtcm-form">
        <div className="rtcm-input-wrapper">
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Write a comment..."
            className="rtcm-input"
            rows={1}
            disabled={submitting}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleSubmit(e);
              }
            }}
          />
          <button
            type="submit"
            className="rtcm-submit"
            disabled={!comment.trim() || submitting}
          >
            <FiSend size={18} />
          </button>
        </div>
      </form>
    </div>
  );
};

export default TaskComments;
